/**
 * TEMP-H-015: Connection Status Indicator
 * 
 * Shows WebSocket connection status and polling fallback state.
 * Provides non-blocking warnings and connection controls.
 */

import React from 'react';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  AlertTriangle, 
  Clock,
  ShieldAlert 
} from 'lucide-react';
import type { ConnectionStatus } from '@/lib/websocket-bridge-ephemeral';

export interface ConnectionStatusProps {
  status: ConnectionStatus;
  isPollingFallback: boolean;
  error: string | null;
  lastUpdate: Date | null;
  onReconnect?: () => void;
  className?: string;
  compact?: boolean;
}

export const ConnectionStatusIndicator: React.FC<ConnectionStatusProps> = ({
  status,
  isPollingFallback,
  error,
  lastUpdate,
  onReconnect,
  className = '',
  compact = false
}) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'connected':
        return {
          icon: <Wifi className="w-4 h-4 text-green-400" />,
          text: 'Connected',
          bgColor: 'bg-green-500/20',
          textColor: 'text-green-300',
          description: 'Real-time updates active'
        };
      
      case 'connecting':
        return {
          icon: <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />,
          text: 'Connecting',
          bgColor: 'bg-blue-500/20',
          textColor: 'text-blue-300',
          description: 'Establishing secure connection...'
        };
      
      case 'auth_failed':
        return {
          icon: <ShieldAlert className="w-4 h-4 text-red-400" />,
          text: 'Auth Failed',
          bgColor: 'bg-red-500/20',
          textColor: 'text-red-300',
          description: isPollingFallback ? 'Using polling updates' : 'Authentication required'
        };
      
      case 'fallback':
        return {
          icon: <Clock className="w-4 h-4 text-yellow-400" />,
          text: 'Polling Mode',
          bgColor: 'bg-yellow-500/20',
          textColor: 'text-yellow-300',
          description: 'Limited updates (5s intervals)'
        };
      
      case 'disconnected':
      default:
        return {
          icon: <WifiOff className="w-4 h-4 text-gray-400" />,
          text: 'Disconnected',
          bgColor: 'bg-gray-500/20',
          textColor: 'text-gray-300',
          description: 'No real-time updates'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const showWarning = status === 'auth_failed' || status === 'fallback' || error;
  
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${statusInfo.bgColor} ${className}`}
        title={statusInfo.description}
      >
        {statusInfo.icon}
        <span className={statusInfo.textColor}>
          {statusInfo.text}
        </span>
        {onReconnect && status !== 'connected' && (
          <button
            onClick={onReconnect}
            className="ml-1 text-gray-400 hover:text-gray-200 transition-colors"
            title="Reconnect"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`${statusInfo.bgColor} border border-gray-600 rounded-lg p-3 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {statusInfo.icon}
          <span className={`text-sm font-medium ${statusInfo.textColor}`}>
            {statusInfo.text}
          </span>
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 truncate">
            {statusInfo.description}
          </p>
        </div>

        {onReconnect && status !== 'connected' && (
          <button
            onClick={onReconnect}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
      </div>

      {/* Warning message for auth failures or errors */}
      {showWarning && (
        <div className="mt-2 pt-2 border-t border-gray-600">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-yellow-200">
                {error || 
                 (status === 'auth_failed' ? 'WebSocket authentication failed. Using polling for updates.' :
                  status === 'fallback' ? 'WebSocket connection issues. Updates may be delayed.' :
                  'Connection issues detected.')}
              </p>
              {status === 'fallback' && (
                <p className="text-xs text-gray-400 mt-1">
                  Updates every 5 seconds. Refresh page to retry WebSocket.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Last update timestamp */}
      {lastUpdate && (
        <div className="mt-2 pt-2 border-t border-gray-600">
          <p className="text-xs text-gray-500">
            Last update: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Minimal connection dot indicator for headers/toolbars
 */
export const ConnectionDot: React.FC<Pick<ConnectionStatusProps, 'status' | 'isPollingFallback'>> = ({
  status,
  isPollingFallback
}) => {
  const getColor = () => {
    switch (status) {
      case 'connected': return 'bg-green-400';
      case 'connecting': return 'bg-blue-400 animate-pulse';
      case 'auth_failed': return isPollingFallback ? 'bg-yellow-400' : 'bg-red-400';
      case 'fallback': return 'bg-yellow-400';
      default: return 'bg-gray-400';
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'connected': return 'Connected - Real-time updates';
      case 'connecting': return 'Connecting...';
      case 'auth_failed': return isPollingFallback ? 'Polling fallback active' : 'Authentication failed';
      case 'fallback': return 'Polling mode - Updates every 5s';
      default: return 'Disconnected';
    }
  };

  return (
    <div 
      className={`w-2 h-2 rounded-full ${getColor()}`}
      title={getTitle()}
      role="status"
      aria-label={getTitle()}
    />
  );
};